import { proxyContainer } from '../polyfill';
import { Keys, ObserverCallback, Observers, Store, StoreSettings } from '../store.types';

function valueHasChanged(value: unknown, old: unknown): boolean {
    // This ensures (old==NaN, value==NaN) always returns false
    return old !== value && (old === old || value === value);
}

export function createStore<State extends object, Actions extends string>(
    settings: StoreSettings<State, Actions>,
): Store<State, Actions> {
    const observers: Observers<State> = [];

    let prevState: State = settings.initialState;

    const validator = {
        set(state: State, key: keyof State, value: any) {
            if (valueHasChanged(state[key], value)) {
                state[key] = value;
                callObservers(state, key);
            }

            return true;
        },
    };

    let state = proxyContainer<State>(settings.initialState || {}, validator);

    function subscribe(observer: ObserverCallback<State>, keys?: Keys<State>) {
        if (typeof observer !== 'function')
            new Error('You can only subscribe to Store changes with a valid function!');
        observers.push({ callback: observer, keys });
        return true;
    }

    function unsubscribe(observer: ObserverCallback<State>) {
        if (typeof observer !== 'function')
            new Error('You can only subscribe to Store changes with a valid function!');

        const match = observers.find(({ callback }) => callback === observer);
        if (match) {
            observers.splice(observers.indexOf(match), 1);
        }
    }

    async function dispatch(actionKey: Actions, payload: any) {
        if (!settings.actions || typeof settings.actions[actionKey] !== 'function')
            new Error(`Action "${actionKey}" doesn't exist.`);

        const action = settings.actions![actionKey];
        prevState = Object.assign({}, state);
        const newState = await action(state, payload);
        state = newState;

        return state;
    }

    function callObservers(data: State, key: any) {
        observers.forEach(({ keys, callback }) => {
            if (!keys) {
                callback(data);
            } else if (Array.isArray(keys) && keys.indexOf(key) > -1) {
                callback(data);
            }
        });
    }

    return {
        subscribe,
        unsubscribe,
        dispatch,
        getState: () => state,
        getPrevState: () => prevState,
    };
}
